import { useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';

import { calcularMes, mesAIso } from '@leadsphere/types';

import { useApi } from '../../hooks/useApi.js';
import type { DashboardArea, DashboardResult } from './types.js';
import { useTicketsTodos } from './useTicketsTodos.js';

const COLS = 10;

function fmtProm(v: number | null | undefined, dash = '-'): string {
  return v === null || v === undefined ? dash : v.toFixed(1);
}

function fmtPct(v: number | null | undefined): string {
  return v === null || v === undefined ? '—' : `${v}%`;
}

// SRS-02 §11.3 — verde ≥80%, ámbar 60-79%, rojo <60%. Usada para % SLA Resp/Reso.
function colorPct(pct: number | null | undefined): string {
  if (pct === null || pct === undefined) return '';
  if (pct >= 80) return 'dash-good';
  if (pct >= 60) return 'dash-warn';
  return 'dash-bad';
}

// Regla distinta e invertida (mockup líneas 2338): alto retrabajo es malo, pero
// es un umbral binario (>20% → rojo), NO la escala de 3 niveles de colorPct.
function colorRetrabajo(pct: number | null | undefined): string {
  if (pct === null || pct === undefined) return 'dash-num';
  return pct > 20 ? 'dash-bad' : 'dash-num';
}

export function TicketsDashboardPage() {
  const { get } = useApi();

  // Meses disponibles: derivados de tickets reales en BD, no hardcodeados
  // (mismo dataset amplio ya usado para los stat-cards del panel).
  const { data: statsData } = useTicketsTodos();

  const mesesDisponibles = useMemo(() => {
    const set = new Set((statsData?.data ?? []).map(t => t.mes));
    return [...set].sort((a, b) => mesAIso(a).localeCompare(mesAIso(b)));
  }, [statsData]);

  const [mesSeleccionado, setMesSeleccionado] = useState<string>(() => calcularMes(new Date()));

  const mesIso = mesAIso(mesSeleccionado);

  const { data: dashboard, isLoading } = useQuery({
    queryKey: ['tickets-dashboard', mesIso],
    queryFn: () => get<DashboardResult>(`/tickets/dashboard?mes=${mesIso}`),
  });

  function renderAreaRow(a: DashboardArea) {
    if (a.sin_datos) {
      return (
        <tr key={a.area}>
          <td style={{ color: 'var(--muted)' }}>{a.area}</td>
          <td colSpan={COLS - 1} style={{ color: 'var(--muted)', fontSize: 12 }}>
            Sin tickets
          </td>
        </tr>
      );
    }

    return (
      <tr key={a.area}>
        <td style={{ fontWeight: 600 }}>{a.area}</td>
        <td className="dash-num">{a.solicitudes}</td>
        <td className="dash-num">{fmtProm(a.tpr_prom)}</td>
        <td className="dash-num">{fmtProm(a.ttr_prom)}</td>
        <td className={colorPct(a.sla_resp_pct)}>{fmtPct(a.sla_resp_pct)}</td>
        <td className={colorPct(a.sla_reso_pct)}>{fmtPct(a.sla_reso_pct)}</td>
        <td className="dash-num">{a.retrabajo_n}</td>
        <td className={colorRetrabajo(a.retrabajo_pct)}>{fmtPct(a.retrabajo_pct)}</td>
        <td>{fmtProm(a.csat_prom, '—')}</td>
        <td className="dash-num">{fmtPct(a.csat_tasa_pct)}</td>
      </tr>
    );
  }

  return (
    <div>
      <div style={{ marginBottom: 6 }}>
        <div style={{ fontSize: 19, fontWeight: 700, color: 'var(--heading)' }}>Dashboard Mensual</div>
        <div style={{ fontSize: 13, color: 'var(--muted)', marginTop: 2 }}>KPIs de servicio interno por área</div>
      </div>

      <div className="dash-controls">
        <span style={{ fontSize: 13, color: 'var(--muted)' }}>Mes:</span>
        {mesesDisponibles.length === 0 ? (
          <button className="dash-month-btn active">{mesSeleccionado}</button>
        ) : (
          mesesDisponibles.map(m => (
            <button
              key={m}
              className={`dash-month-btn${m === mesSeleccionado ? ' active' : ''}`}
              onClick={() => setMesSeleccionado(m)}
            >
              {m}
            </button>
          ))
        )}
      </div>

      <div className="table-wrap">
        <div className="table-scroll">
          <table className="dash-table">
            <thead>
              <tr>
                <th>Área</th>
                <th>Solicitudes</th>
                <th>Prom. TPR (hrs)</th>
                <th>Prom. TTR (hrs)</th>
                <th>% SLA Resp</th>
                <th>% SLA Reso</th>
                <th>Retrabajo</th>
                <th>% Retrabajo</th>
                <th>CSAT prom.</th>
                <th>Tasa CSAT</th>
              </tr>
            </thead>
            <tbody>
              {isLoading || !dashboard ? (
                <tr>
                  <td className="empty-td" colSpan={COLS}>
                    Cargando…
                  </td>
                </tr>
              ) : (
                <>
                  {dashboard.areas.map(renderAreaRow)}
                  <tr className="total-row">
                    <td>TOTAL</td>
                    <td>{dashboard.total.solicitudes}</td>
                    <td>{fmtProm(dashboard.total.tpr_prom)}</td>
                    <td>{fmtProm(dashboard.total.ttr_prom)}</td>
                    <td>{fmtPct(dashboard.total.sla_resp_pct)}</td>
                    <td>{fmtPct(dashboard.total.sla_reso_pct)}</td>
                    <td>{dashboard.total.retrabajo_n}</td>
                    <td>{fmtPct(dashboard.total.retrabajo_pct)}</td>
                    <td>{fmtProm(dashboard.total.csat_prom, '—')}</td>
                    <td>{fmtPct(dashboard.total.csat_tasa_pct)}</td>
                  </tr>
                </>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
